/** @format */

import {
	ChangeDetectionStrategy,
	Component,
	computed,
	DestroyRef,
	effect,
	ElementRef,
	inject,
	input,
	OnDestroy,
	OnInit,
	PLATFORM_ID,
	signal,
	viewChild
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { CompareGridComponent } from '@compare/components';
import { CompareBarProduct, CompareProductDetails, CompareService } from '@compare/data-access';
import { ProcessedNonBundleProduct } from '@shared/data-access/interfaces';
import { Scene7Service } from '@shared/data-access/services';
import {
	CertonaRecommendationsResponse,
	CertonaScheme,
	CertonaService
} from '@product-recommendations/data-access';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, finalize } from 'rxjs/operators';
import { HmfButtonComponent } from '@dsg-tech/homefield-angular';
import {
	homefieldIconChevronDown,
	homefieldIconChevronUp,
	HomefieldIconLibraryModule,
	HomefieldIconsLibraryService
} from '@dsg-tech/homefield-angular-icons';
import { DCSG_CONSTANTS } from '@shared/data-access/constants';

@Component({
	selector: 'pdp-component-compare-section',
	imports: [CompareGridComponent, HmfButtonComponent, HomefieldIconLibraryModule],
	templateUrl: './compare-section.component.html',
	styleUrl: './compare-section.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class CompareSectionComponent implements OnInit, OnDestroy {
	private readonly destroyRef = inject(DestroyRef);
	private readonly elementRef = inject(ElementRef);
	private readonly platformId = inject(PLATFORM_ID);
	private readonly certonaService = inject(CertonaService);
	private readonly compareService = inject(CompareService);
	private readonly scene7Service = inject(Scene7Service);
	private readonly homefieldIconsLibraryService = inject(HomefieldIconsLibraryService);

	readonly currentProduct = input.required<ProcessedNonBundleProduct>();
	readonly currentProductSelectedColor = input.required<string>();
	readonly wrapperClasses = input<string>();

	readonly compareGrid = viewChild('compareGrid', { read: ElementRef });

	readonly currentCompareBarProduct = computed<CompareBarProduct>(() => {
		const currentProductImage = this.scene7Service.getProductImageUrl(
			this.currentProduct().partNumber,
			'ProductBean',
			this.currentProductSelectedColor(),
			undefined,
			{ wid: '355', hei: '355', fmt: 'pjpeg' },
			false
		);

		return {
			imgURL: currentProductImage,
			ecode: this.currentProduct().partNumber,
			name: this.currentProduct().name,
			pdpURL: this.currentProduct().pdpSeoUrl,
			gridImageURL: currentProductImage,
			compareSource: 'currentProduct'
		};
	});

	readonly certonaMonetateLoading = signal(true);
	private readonly certonaCompareBarProducts = signal<CompareBarProduct[]>([]);

	readonly compareBarProducts = computed(() => [
		this.currentCompareBarProduct(),
		...this.certonaCompareBarProducts()
	]);
	private readonly compareProductEcodes = computed(() =>
		this.compareBarProducts().map((product) => product.ecode)
	);

	readonly compareProductDetails = signal<CompareProductDetails | null>(null);
	readonly compareProductDetailsErrored = signal(false);
	readonly compareProductDetailsLoading = signal(false);
	readonly showCompare = computed(() => {
		const certonaCompareBarProductsSet = !!this.certonaCompareBarProducts().length;
		const compareProductDetailsErrored = this.compareProductDetailsErrored();
		return certonaCompareBarProductsSet && !compareProductDetailsErrored;
	});
	readonly loading = computed(() => {
		const certonaMonetateLoading = this.certonaMonetateLoading();
		const compareProductDetailsLoading = this.compareProductDetailsLoading();
		return certonaMonetateLoading || compareProductDetailsLoading;
	});

	readonly collapsed = signal(true);
	readonly expandHeight = signal<number>(null); // Gets properly set on view init
	readonly collapseContainerHeight = computed(() => {
		const collapsed = this.collapsed();
		const expandHeight = this.expandHeight();

		return collapsed ? 720 : expandHeight;
	});

	private readonly certonaMonetateScheme = 'product_rr';

	readonly gridResizeObserver: ResizeObserver;

	constructor() {
		this.homefieldIconsLibraryService.registerIcons([
			homefieldIconChevronDown,
			homefieldIconChevronUp
		]);

		// Update compare products when selected color changes or Certona/Monetate response comes in
		effect(() => {
			const ecodes = this.compareProductEcodes();

			// Only get ecode product details when there is more than 1 non-empty ecode (empty ecodes = monetate / certona loading)
			if (ecodes.filter((ecode) => !!ecode).length > 1) {
				this.compareProductDetailsErrored.set(false);
				this.compareProductDetailsLoading.set(true);
				this.certonaMonetateLoading.set(false);
				this.compareService
					.getProductDetails(ecodes, false)
					.pipe(
						takeUntilDestroyed(this.destroyRef),
						finalize(() => this.compareProductDetailsLoading.set(false))
					)
					.subscribe({
						next: (productDetails) => {
							this.compareProductDetails.set(productDetails);
						},
						error: () => {
							this.compareProductDetailsErrored.set(true);
						}
					});
			}
		});

		// Whenever the compare grid's content height changes, update the expanded container's height
		// So when "View More" is clicked it expands to the correct full height
		if (isPlatformBrowser(this.platformId)) {
			this.gridResizeObserver = new ResizeObserver((entries) => {
				for (const entry of entries) {
					const gridHeight = entry.contentBoxSize
						? entry.contentBoxSize[0].blockSize
						: entry.contentRect.height;
					if (this.expandHeight() !== gridHeight) {
						this.expandHeight.set(gridHeight);
					}
				}
			});
		}

		// Set up resize observer once grid is rendered
		effect(() => {
			if (this.compareGrid()) {
				// Just in case this somehow is triggered twice -- in testing that never happened
				this.gridResizeObserver.disconnect();
				this.gridResizeObserver.observe(this.compareGrid().nativeElement);
			}
		});
	}

	ngOnInit() {
		this.certonaService.certonaRecommendationsState
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				// see product-recommendations.component.ts:241
				filter(
					(res) =>
						res?.certonaRecommendations?.resonance?.schemes?.find(
							(s: CertonaScheme) => s.scheme === this.certonaMonetateScheme
						) || res?.certonaRecommendations?.source === 'Monetate'
				)
			)
			.subscribe((response: CertonaRecommendationsResponse) =>
				this.handleCertonaMonetateResponse(response)
			);

		if (isPlatformBrowser(this.platformId)) {
			setTimeout(() => {
				this.certonaMonetateLoading.set(false);
			}, DCSG_CONSTANTS.productRecs.timeout);
		}
	}

	ngOnDestroy() {
		if (this.gridResizeObserver) {
			this.gridResizeObserver.disconnect();
		}
	}

	toggleSectionCollapsed() {
		this.collapsed.set(!this.collapsed());
		if (this.collapsed()) {
			this.elementRef.nativeElement.scrollIntoView({ behavior: 'smooth' });
		}
	}

	private handleCertonaMonetateResponse(response: CertonaRecommendationsResponse) {
		if (!response?.certonaRecommendations?.resonance?.schemes) {
			return;
		}

		const schemas = response.certonaRecommendations.resonance.schemes.filter(
			(schema) => schema.scheme === this.certonaMonetateScheme
		);

		if (schemas.length === 0 && response.certonaRecommendations.source === 'Monetate') {
			// If Monetate recs didn't include this scheme, treat it as if it included an empty one.
			// This is because Monetate sometimes leaves out empty schemas altogether,
			// instead of sending empty ones with `display: "no"` like Certona does.
			schemas.push({
				display: 'no',
				explanation: '',
				items: [],
				scheme: this.certonaMonetateScheme
			});
		}

		const newItems = schemas
			.map((schema) => schema.items)
			.flat()
			.slice(0, 4);

		if (!newItems.length) {
			this.certonaCompareBarProducts.set([]);
			return;
		}

		const newCompareBarProducts: CompareBarProduct[] = newItems.map((item) => {
			if (item) {
				return {
					ecode: item.ID,
					imgURL: `${item.ImageURL}&wid=355&hei=355&fmt=pjpeg`,
					gridImageURL: `${item.ImageURL}&wid=355&hei=355&fmt=pjpeg`,
					name: item.Name,
					pdpURL: item.URL
				};
			} else {
				// For skeleton loaders
				return {
					ecode: '',
					imgURL: '',
					gridImageURL: '',
					name: '',
					pdpURL: ''
				};
			}
		});

		this.certonaCompareBarProducts.set(newCompareBarProducts);
	}
}
